// Jotly Desktop — Talk surface (per ADR-0009 + the Jotly Talk Flow design).
//
// The wedge home: a centre conversation column (the spine — capture AND ask in
// one box, no +Jot button) flanked by a calm right rail that surfaces only what
// matters: the next meeting, a calendar peek, the one no-pressure item, a
// projects glance. No tidy dashboard, no folders — "just ask".
//
// Everything is wired to real endpoints via useTalk (POST /messages capture-ack
// + POST /chat/ask recall) and useCalendarPeek (GET /calendar/events). The rail
// is deliberately thin: tapping a card posts into the centre chat rather than
// opening a new surface.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconJD } from './atoms';
import { MeetingBriefModal, type PreppedNote, type PersonBrief } from './MeetingBriefModal';
import { type TalkTurn } from '../../hooks/useTalk';
import { useCalendarPeek, type CalendarPeek } from '../../hooks/useCalendarPeek';
import { useFocusItem } from '../../hooks/useFocusItem';
import { useProjects } from '../../hooks/useProjects';
import { listTasks, listNotes, updateTaskStatus, type CalendarEvent } from '../../api/client';
import type { TaskItem, NoteResponse } from '../../api/types';
import { resolveMeetingPeople } from '../../utils/people';
import { fileToBase64, firstImageFile } from '../../api/capture';

export interface TalkController {
  turns: TalkTurn[];
  busy: boolean;
  send: (text: string) => void;
  sendImage: (b64: string, label?: string) => void;
  ask: (question: string) => void;
}

const PROMPTS: Array<{ pre: string; q: string }> = [
  { pre: 'Next', q: "What's next for me?" },
  { pre: 'Today', q: 'What fits around my day?' },
  { pre: 'Promised', q: 'What did I promise anyone?' },
  { pre: 'Pressing', q: "What's pressing?" },
  { pre: 'Week', q: "How's my week looking?" },
];

function greetingLine(): string {
  const h = new Date().getHours();
  const part = h < 12 ? 'Morning' : h < 18 ? 'Afternoon' : 'Evening';
  return `${part}. It’s all here if you need it — jot a thought, paste a link, drop a screenshot, or just ask.`;
}

export function TalkScreen({ talk, track = 'all' }: { talk: TalkController; track?: string }) {
  const { turns, send, sendImage, ask } = talk;
  const [dragOver, setDragOver] = useState(false);
  const [threadSearch, setThreadSearch] = useState('');
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = firstImageFile(e.dataTransfer);
      if (!file) return;
      try {
        const b64 = await fileToBase64(file);
        await sendImage(b64, file.name || 'Screenshot');
      } catch {
        // fileToBase64 rejects loudly enough; nothing to recover here.
      }
    },
    [sendImage],
  );

  const searchLower = threadSearch.trim().toLowerCase();
  const matchedIds = useMemo(() => {
    if (!searchLower) return null;
    const ids = new Set<string>();
    for (const t of turns) {
      if (t.text?.toLowerCase().includes(searchLower)) ids.add(t.id);
    }
    return ids;
  }, [turns, searchLower]);

  const firstMatchRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (matchedIds && matchedIds.size > 0 && firstMatchRef.current) {
      firstMatchRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [matchedIds]);

  return (
    <div className="jd-talk">
      <section
        className={`jd-talk-main ${dragOver ? 'drag-over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {turns.length > 3 && (
          <ThreadSearch value={threadSearch} onChange={setThreadSearch} matchCount={matchedIds?.size ?? 0} active={searchLower.length > 0} />
        )}

        <div className="jd-talk-thread" ref={bodyRef}>
          <TalkGreeting />
          {turns.map((t, i) => {
            const highlighted = matchedIds ? matchedIds.has(t.id) : false;
            const isFirstMatch = highlighted && (i === 0 || !matchedIds!.has(turns[i - 1]?.id));
            return (
              <div key={t.id} ref={isFirstMatch ? firstMatchRef : undefined} className={highlighted ? 'jd-talk-match' : undefined}>
                <TalkBubble turn={t} onCorrect={send} />
              </div>
            );
          })}
        </div>

        <div className="jd-chips">
          {PROMPTS.map((p) => (
            <button key={p.pre} type="button" className="jd-chip" onClick={() => ask(p.q)}>
              <b>{p.pre}</b> {p.q}
            </button>
          ))}
        </div>

        <Composer onSend={send} onImage={sendImage} />

        {dragOver && (
          <div className="jd-talk-dropzone">
            <IconJD name="image" size={20} stroke={1.8} color="currentColor" />
            Drop to capture — I&apos;ll read it
          </div>
        )}
      </section>

      <RightRail onAsk={ask} track={track} />
    </div>
  );
}

// ─── Greeting — warm first message, always present ─────────────────────────

function TalkGreeting() {
  const [line] = useState(greetingLine);
  return (
    <div className="jd-talk-bubble jotly">
      <div className="jd-talk-bubble-body">
        <span className="jd-talk-greeting">{line}</span>
      </div>
    </div>
  );
}

// ─── In-thread search (the "scroll back" proposed feature) ──────────────────

function ThreadSearch({
  value,
  onChange,
  matchCount,
  active,
}: {
  value: string;
  onChange: (v: string) => void;
  matchCount: number;
  active: boolean;
}) {
  return (
    <div className="jd-thread-search">
      <IconJD name="search" size={14} stroke={2} color="currentColor" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search this conversation…"
      />
      {active && (
        <span className="jd-thread-search-count">
          {matchCount} {matchCount === 1 ? 'match' : 'matches'}
        </span>
      )}
      {active && (
        <button type="button" className="jd-thread-search-clear" onClick={() => onChange('')} title="Clear">
          <IconJD name="x" size={12} stroke={2.2} color="currentColor" />
        </button>
      )}
    </div>
  );
}

// ─── A single conversation turn ──────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  TASK: 'task',
  MULTI_TASK: 'tasks',
  DUMP: 'thought',
  SHOPPING: 'shopping',
  RECURRING: 'recurring',
  RELAY: 'relay',
  NOTE: 'note',
};

function correctionText(currentType: string): string | null {
  if (currentType === 'TASK' || currentType === 'MULTI_TASK' || currentType === 'RECURRING')
    return "that last one isn't a task, just a thought";
  if (currentType === 'DUMP')
    return 'actually that last one is a task';
  return null;
}

function TalkBubble({ turn, onCorrect }: { turn: TalkTurn; onCorrect: (text: string) => void }) {
  if (turn.role === 'you') {
    return <div className="jd-talk-bubble you">{turn.text}</div>;
  }
  const showCorrection = turn.jotted && !turn.pending && turn.messageType;
  const typeLabel = turn.messageType ? TYPE_LABELS[turn.messageType] ?? turn.messageType.toLowerCase() : null;
  const correction = turn.messageType ? correctionText(turn.messageType) : null;
  const isCapture = turn.kind === 'capture';
  const isRecall = turn.kind === 'recall';

  return (
    <div className="jd-talk-bubble jotly">
      <div className="jd-talk-bubble-body">
        {isCapture && !turn.pending && (
          <div className="jd-mini">
            <IconJD name="sparkle" size={11} stroke={2} color="currentColor" />
            Jotted &amp; safe
          </div>
        )}
        {isRecall && !turn.pending && turn.citations && turn.citations.length > 0 && (
          <div className="jd-mini">
            <IconJD name="sparkle" size={11} stroke={2} color="currentColor" />
            Found it
          </div>
        )}
        <span className={turn.pending ? 'pending-text' : ''}>{turn.text || (turn.pending ? 'Thinking…' : '')}</span>
        {showCorrection && typeLabel && (
          <span className="jd-type-row">
            <span className="jd-type-tag">{typeLabel}</span>
            {correction && (
              <button type="button" className="jd-type-fix" onClick={() => onCorrect(correction)}>
                not a {typeLabel === 'thought' ? 'thought' : 'task'}
              </button>
            )}
          </span>
        )}
        {turn.citations && turn.citations.length > 0 && (
          <div className="jd-talk-cites">
            {turn.citations.map((c) => (
              <div key={`${c.sourceType}-${c.id}`} className="jd-talk-cite">
                <span className="jt-tag">{c.sourceType}</span>
                <div className="cite-text">
                  <strong>{c.title}</strong>
                  {c.snippet ? <span className="snip"> {c.snippet}</span> : null}
                </div>
              </div>
            ))}
          </div>
        )}
        {isCapture && !turn.pending && (
          <div className="jd-relief">You can let it go now.</div>
        )}
        {isRecall && !turn.pending && turn.text && (
          <div className="jd-relief">Nothing&apos;s lost — even when you can&apos;t name it.</div>
        )}
      </div>
    </div>
  );
}

// ─── Composer — one box for jot AND ask, no +Jot button ──────────────────────

function Composer({
  onSend,
  onImage,
}: {
  onSend: (text: string) => void;
  onImage: (b64: string, label?: string) => void;
}) {
  const [text, setText] = useState('');
  const [listening, setListening] = useState(false);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const recogRef = useRef<SpeechRecognition | null>(null);

  const hasSpeech = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const toggleMic = useCallback(() => {
    if (listening && recogRef.current) {
      recogRef.current.stop();
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = 'en-GB';
    r.interimResults = false;
    r.continuous = false;
    r.onresult = (e: SpeechRecognitionEvent) => {
      const transcript = e.results[0]?.[0]?.transcript;
      if (transcript) {
        setText((prev) => (prev ? `${prev} ${transcript}` : transcript));
      }
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r;
    r.start();
    setListening(true);
  }, [listening]);

  const autoGrow = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  const submit = () => {
    const v = text.trim();
    if (!v) return;
    onSend(v);
    setText('');
    requestAnimationFrame(() => {
      const el = taRef.current;
      if (el) {
        el.style.height = 'auto';
        el.focus();
      }
    });
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const file = firstImageFile(e.clipboardData);
    if (!file) return;
    e.preventDefault();
    try {
      const b64 = await fileToBase64(file);
      onImage(b64, file.name || 'Pasted image');
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="jd-composer">
      <button
        type="button"
        className="jd-composer-icon"
        title="Add a screenshot"
        onClick={() => fileRef.current?.click()}
      >
        <IconJD name="image" size={17} stroke={1.7} color="currentColor" />
      </button>
      <textarea
        ref={taRef}
        className="jd-composer-input"
        value={text}
        rows={1}
        placeholder="Jot, ask, paste a link, or drop a screenshot…"
        onChange={(e) => {
          setText(e.target.value);
          autoGrow();
        }}
        onPaste={handlePaste}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        autoFocus
      />
      {hasSpeech && (
        <button
          type="button"
          className={`jd-composer-icon ${listening ? 'active' : ''}`}
          title={listening ? 'Stop listening' : 'Voice input'}
          onClick={toggleMic}
        >
          <IconJD name="mic" size={17} stroke={1.7} color={listening ? 'var(--accent)' : 'currentColor'} />
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          try {
            const b64 = await fileToBase64(f);
            onImage(b64, f.name || 'Screenshot');
          } catch {
            /* ignore */
          } finally {
            e.target.value = '';
          }
        }}
      />
      <button
        type="button"
        className="jd-composer-send"
        onClick={submit}
        disabled={text.trim().length === 0}
        title="Send (↵)"
      >
        <IconJD name="arrow-up" size={15} stroke={2.4} color="currentColor" />
      </button>
      <div className="jd-composer-hint">
        <IconJD name="sparkle" size={11} stroke={2} color="var(--accent-strong)" />
        Drop a screenshot anywhere · ⌘V to paste a link · one box for jot &amp; ask — no forms, no filing
      </div>
    </div>
  );
}

// ─── Right rail — calm, surfacing-only ───────────────────────────────────────

function RightRail({ onAsk, track = 'all' }: { onAsk: (q: string) => void; track?: string }) {
  const peek = useCalendarPeek();
  const [briefEvent, setBriefEvent] = useState<CalendarEvent | null>(null);
  const [allTasks, setAllTasks] = useState<TaskItem[]>([]);
  const [allNotes, setAllNotes] = useState<NoteResponse[]>([]);
  const [briefData, setBriefData] = useState<{
    person: string;
    people: PersonBrief[];
    bringUp: TaskItem[];
    promises: TaskItem[];
    prepped: PreppedNote[];
    personTags: Map<string, string>;
  } | null>(null);

  useEffect(() => {
    listTasks({ status: 'PENDING' })
      .then((r) => setAllTasks(r.tasks ?? []))
      .catch(() => setAllTasks([]));
    listNotes()
      .then((r) => setAllNotes(r.notes))
      .catch(() => setAllNotes([]));
  }, []);

  const nextCounts = useMemo(() => {
    if (!peek.next || allTasks.length === 0) return { bringUp: 0, promises: 0 };
    const resolved = resolveMeetingPeople(peek.next, allTasks);
    const open = allTasks.filter((t) => t.status === 'PENDING');
    let bu = 0;
    let pr = 0;
    for (const r of resolved) {
      bu += open.filter((t) => (t.bringUpWith ?? []).some((p) => r.matchesField(p))).length;
      pr += open.filter((t) => t.relayTarget && r.matchesField(t.relayTarget)).length;
    }
    return { bringUp: bu, promises: pr };
  }, [peek.next, allTasks]);

  const openBrief = useCallback(
    (event: CalendarEvent) => {
      setBriefEvent(event);
      const resolved = resolveMeetingPeople(event, allTasks);
      if (resolved.length === 0) {
        const fallback = event.attendees?.[0] ?? event.title;
        setBriefData({
          person: fallback,
          people: [],
          bringUp: [],
          promises: [],
          prepped: [],
          personTags: new Map(),
        });
        return;
      }

      const open = allTasks.filter((t) => t.status === 'PENDING');
      const tags = new Map<string, string>();
      const ppl: PersonBrief[] = resolved.map((r) => {
        const bu = open.filter((t) =>
          (t.bringUpWith ?? []).some((p) => r.matchesField(p)),
        );
        const pr = open.filter(
          (t) => t.relayTarget && r.matchesField(t.relayTarget),
        );
        const mentions = (n: NoteResponse) =>
          r.matchesField(n.title || '') ||
          (n.bodyMarkdown || '').toLowerCase().includes(r.display.toLowerCase());
        const prep: PreppedNote[] = allNotes
          .filter(mentions)
          .slice(0, 4)
          .map((n) => ({ id: n.id, title: n.title || 'Untitled note' }));

        if (resolved.length > 1) {
          bu.forEach((t) => tags.set(t.sk, r.display));
          pr.forEach((t) => tags.set(t.sk, r.display));
        }

        return { person: r.display, bringUp: bu, promises: pr, prepped: prep };
      });

      const primary = ppl[0];
      const isMulti = resolved.length > 1;
      setBriefData({
        person: primary.person,
        people: ppl,
        bringUp: isMulti ? ppl.flatMap((p) => p.bringUp) : primary.bringUp,
        promises: isMulti ? ppl.flatMap((p) => p.promises) : primary.promises,
        prepped: isMulti ? ppl.flatMap((p) => p.prepped) : primary.prepped,
        personTags: tags,
      });
    },
    [allTasks, allNotes],
  );

  const toggleDone = useCallback((task: TaskItem) => {
    const next = task.status === 'DONE' ? 'PENDING' : 'DONE';
    setBriefData((prev) => {
      if (!prev) return prev;
      const upd = (arr: TaskItem[]) =>
        arr.map((t) => (t.sk === task.sk ? { ...t, status: next } : t));
      return {
        ...prev,
        bringUp: upd(prev.bringUp),
        promises: upd(prev.promises),
        people: prev.people.map((p) => ({
          ...p,
          bringUp: upd(p.bringUp),
          promises: upd(p.promises),
        })),
      };
    });
    updateTaskStatus(task.sk, next).catch(() => {
      setBriefData((prev) => {
        if (!prev) return prev;
        const rev = (arr: TaskItem[]) =>
          arr.map((t) => (t.sk === task.sk ? { ...t, status: task.status } : t));
        return {
          ...prev,
          bringUp: rev(prev.bringUp),
          promises: rev(prev.promises),
          people: prev.people.map((p) => ({
            ...p,
            bringUp: rev(p.bringUp),
            promises: rev(p.promises),
          })),
        };
      });
    });
  }, []);

  const closeBrief = useCallback(() => {
    setBriefEvent(null);
    setBriefData(null);
  }, []);

  return (
    <aside className="jd-rail-right">
      <CalendarPeekCard
        peek={peek}
        onSeeAll={() => onAsk("What's on today?")}
        onViewBrief={peek.next ? () => openBrief(peek.next!) : undefined}
        bringUpCount={nextCounts.bringUp}
        promiseCount={nextCounts.promises}
      />
      <div className="jd-rail-divider" />
      <FocusItemCard track={track} />
      <div className="jd-rail-divider" />
      <ProjectsGlance onAsk={onAsk} />
      <div className="jd-rail-note">No folders to keep · just ask, and I surface it.</div>

      {briefEvent && briefData && (
        <MeetingBriefModal
          meetingTitle={briefEvent.title}
          person={briefData.person}
          people={briefData.people}
          timeLabel={new Date(briefEvent.startAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          untilLabel={untilLabel(briefEvent.startAt)}
          bringUp={briefData.bringUp}
          promises={briefData.promises}
          prepped={briefData.prepped}
          personTags={briefData.personTags}
          onClose={closeBrief}
          onToggleDone={toggleDone}
          onNoteCreated={closeBrief}
        />
      )}
    </aside>
  );
}

function minutesUntil(startAt: string): number {
  return Math.max(0, Math.round((Date.parse(startAt) - Date.now()) / 60_000));
}

function untilLabel(startAt: string): string {
  const diff = Date.parse(startAt) - Date.now();
  if (diff <= 0) return 'now';
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `in ${mins}m`;
  return `in ${Math.round(mins / 60)}h`;
}

function CalendarPeekCard({
  peek,
  onSeeAll,
  onViewBrief,
  bringUpCount,
  promiseCount,
}: {
  peek: CalendarPeek;
  onSeeAll: () => void;
  onViewBrief?: () => void;
  bringUpCount: number;
  promiseCount: number;
}) {
  if (peek.unavailable) {
    return (
      <div className="jd-rcard">
        <div className="jd-rcard-eyebrow">
          <IconJD name="cal" size={11} stroke={2} color="currentColor" />
          Calendar
        </div>
        <div className="jd-rcard-sub">Not connected. Ask me to set it up.</div>
      </div>
    );
  }
  if (peek.failed) {
    return (
      <div className="jd-rcard">
        <div className="jd-rcard-eyebrow">
          <IconJD name="cal" size={11} stroke={2} color="currentColor" />
          Calendar
        </div>
        <div className="jd-rcard-sub">Couldn&apos;t reach your calendar just now.</div>
      </div>
    );
  }
  if (peek.isLoading) {
    return (
      <div className="jd-rcard">
        <div className="jd-rcard-eyebrow">
          <IconJD name="cal" size={11} stroke={2} color="currentColor" />
          Calendar
        </div>
        <div className="jd-rcard-sub">Checking today…</div>
      </div>
    );
  }
  if (!peek.next) {
    return (
      <div className="jd-rcard">
        <div className="jd-rcard-eyebrow">
          <IconJD name="cal" size={11} stroke={2} color="currentColor" />
          Calendar
        </div>
        <div className="jd-rcard-sub">Nothing else on the calendar today.</div>
      </div>
    );
  }
  const mins = minutesUntil(peek.next.startAt);
  const person = peek.next.attendees?.[0];
  return (
    <div className="jd-rcard jd-rcard-meeting">
      <div className="jd-rcard-eyebrow">
        <IconJD name="clock" size={11} stroke={2} color="currentColor" />
        Next up {mins > 0 ? `· in ${mins} min` : '· now'}
      </div>
      <div className="jd-rcard-title jd-rcard-title-big">{peek.next.title}</div>
      <div className="jd-rcard-pills">
        {bringUpCount > 0 && <span className="jd-pill key">{bringUpCount} to bring up</span>}
        {promiseCount > 0 && <span className="jd-pill">{promiseCount} {promiseCount === 1 ? 'promise' : 'promises'}</span>}
      </div>
      {onViewBrief && (
        <button type="button" className="jd-rcard-brief-link" onClick={onViewBrief}>
          <IconJD name="sparkle" size={11} stroke={2} color="currentColor" />
          {person ? `Open brief — all things ${person} →` : 'Open meeting brief →'}
        </button>
      )}
      {peek.moreCount > 0 && (
        <button type="button" className="jd-cal-peek-row" onClick={onSeeAll}>
          <IconJD name="clock" size={13} stroke={1.9} color="currentColor" />
          <span>+ {peek.moreCount} more on the calendar today</span>
          <span className="jd-cal-peek-link">see all →</span>
        </button>
      )}
    </div>
  );
}

function FocusItemCard({ track = 'all' }: { track?: string }) {
  const { item, isLoading } = useFocusItem(track);

  if (isLoading) {
    return (
      <div className="jd-rcard quiet">
        <div className="jd-rcard-eyebrow dim">
          <IconJD name="sparkle" size={11} stroke={2} color="currentColor" />
          Next · no pressure
        </div>
        <div className="jd-rcard-sub">Checking…</div>
      </div>
    );
  }
  if (!item) {
    return (
      <div className="jd-rcard quiet">
        <div className="jd-rcard-eyebrow dim">
          <IconJD name="sparkle" size={11} stroke={2} color="currentColor" />
          Next · no pressure
        </div>
        <div className="jd-rcard-sub">Nothing waiting on you right now.</div>
      </div>
    );
  }
  return (
    <div className="jd-rcard quiet">
      <div className="jd-rcard-eyebrow dim">
        <IconJD name="sparkle" size={11} stroke={2} color="currentColor" />
        Next · no pressure
      </div>
      <div className="jd-rcard-title jd-rcard-focus-title">{item.title}</div>
      {item.dueDate && (
        <div className="jd-rcard-sub jd-rcard-focus-why">
          The only hard deadline this week — due{' '}
          {new Date(item.dueDate).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })}
        </div>
      )}
    </div>
  );
}

function ProjectsGlance({ onAsk }: { onAsk: (q: string) => void }) {
  const { projects, isLoading } = useProjects();

  if (isLoading) {
    return (
      <div className="jd-rcard">
        <div className="jd-rcard-eyebrow">Projects</div>
        <div className="jd-rcard-sub">Loading…</div>
      </div>
    );
  }
  if (projects.length === 0) {
    return (
      <div className="jd-rcard">
        <div className="jd-rcard-eyebrow">Projects</div>
        <div className="jd-rcard-sub">Nothing grouped yet.</div>
      </div>
    );
  }
  return (
    <div className="jd-rcard">
      <div className="jd-rcard-eyebrow">Projects</div>
      <div className="jd-rail-projects">
        {projects.map((p) => (
          <button
            key={p.sk}
            type="button"
            className="jd-rail-project tappable"
            onClick={() => onAsk(`Where am I with ${p.name}?`)}
          >
            <div className="jd-rp-info">
              <span className="jd-rp-name">{p.name}</span>
              <span className="jd-rp-glance">{p.status}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
